import React, {ReactElement, useEffect, useState} from "react";
import {useLocation} from "react-router-dom";
import {ModalProps, Modal} from "../../components/Modal";

export const ModalContext = React.createContext<ModalContext>({
  showModal: () => undefined,
  hideModal: () => undefined,
});

type ModalContext = {
  showModal: (modalProps: ModalProps) => void;
  hideModal: () => void;
};

function ModalProvider({children}: {children: ReactElement}) {
  const [currentModal, setCurrentModal] = useState<ModalProps | null>(null);
  const location = useLocation();
  const [originalTabIndices, setOriginalTabIndices] = useState<
    {element: Element; tabindex: string | null}[]
  >([]);

  // Hide modal on location change
  useEffect(() => {
    hideModal();
  }, [location]);

  // Hide modal on Escape keydown
  useEffect(() => {
    if (currentModal) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") hideModal();
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [currentModal]);

  // Disable tab navigation on background elements while modal is shown
  const disableTabNavigation = () => {
    // get all focusable elements
    const mainAndHeaderElements = document.querySelectorAll(
      ".landingPageContent, .landingPageGlobalMenu, header",
    );
    const focusableSelectors =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusableElements = Array.from(mainAndHeaderElements).flatMap(
      (element) => Array.from(element.querySelectorAll(focusableSelectors)),
    );

    // store the original tabindex
    const tabIndices = focusableElements.map((element) => {
      const originalTabindex = element.getAttribute("tabindex");
      element.setAttribute("tabindex", "-1");
      return {element: element, tabindex: originalTabindex};
    });
    setOriginalTabIndices(tabIndices);
  };

  const restoreTabNavigation = () => {
    originalTabIndices.forEach((item) => {
      if (item.tabindex !== null) {
        item.element.setAttribute("tabindex", item.tabindex);
      } else {
        item.element.removeAttribute("tabindex");
      }
    });
    setOriginalTabIndices([]);
  };

  const showModal = (props: ModalProps) => {
    setCurrentModal(props);
    disableTabNavigation();
  };

  const hideModal = () => {
    setCurrentModal(null);
    restoreTabNavigation();
  };

  return (
    <ModalContext.Provider value={{showModal, hideModal}}>
      {children}
      {currentModal && <Modal {...currentModal} />}
    </ModalContext.Provider>
  );
}
export {ModalProvider};
